import { useQuery } from '../../lib/query';
import { Link } from '../../lib/router';
import { itinerariesCrud, detailOf, requestOf } from './itinerariesConfig';
import { toGeneratedImageUrl } from '../../lib/images';

const imgUrl = (f) => toGeneratedImageUrl(f);

function fmtDate(s) {
  if (!s) return '—';
  const d = new Date(s);
  return isNaN(d) ? s : d.toLocaleString();
}

// Read-only detail view for an AI-generated itinerary record.
export default function ItineraryDetailPage({ params }) {
  const { data: row, isLoading, error } = useQuery({
    queryKey: ['itineraries', params.id],
    queryFn: () => itinerariesCrud.get(params.id),
  });

  if (isLoading) return <div className="state state--loading">Loading…</div>;
  if (error) return <div className="state state--error" role="alert">{error.message}</div>;
  if (!row) return null;

  const det = detailOf(row);
  const req = requestOf(row);
  const days = Array.isArray(det?.itinerary) ? det.itinerary : [];
  const hotels = Array.isArray(det?.hotels) ? det.hotels : [];
  const images = Array.isArray(det?.images) ? det.images : [];

  return (
    <div className="page">
      <div className="page__header">
        <h1>{det?.name || `Itinerary #${row.id}`}</h1>
        <Link to="/itineraries" className="btn btn--ghost">← Back</Link>
      </div>

      {!det ? (
        <div className="state state--empty">This record has no parsed itinerary (status: {row.status || '—'}).</div>
      ) : (
        <>
          <div className="detail">
            <p><strong>Destination:</strong> {[det.city, det.state].filter(Boolean).join(', ') || '—'}</p>
            <p><strong>Duration:</strong> {det.total_days || req.trip_duration || '—'} days</p>
            <p><strong>Est. price:</strong> {det.price_estimated_range || '—'}</p>
            <p><strong>Group:</strong> {[req.travel_group_type, req.number_of_people && `${req.number_of_people} people`, req.food_preferences && `${req.food_preferences} food`].filter(Boolean).join(' · ') || '—'}</p>
            {Array.isArray(req.preferred_activities) && req.preferred_activities.length > 0 && (
              <p><strong>Preferences:</strong> {req.preferred_activities.join(', ')}</p>
            )}
            <p><strong>Status:</strong> {row.status || '—'} · <strong>Tokens:</strong> {(row.input_token ?? 0)} in / {(row.output_token ?? 0)} out · <strong>Created:</strong> {fmtDate(row.created_at)}</p>
            {det.description && <p className="muted">{det.description}</p>}
            {det.notes && <p className="muted"><em>{det.notes}</em></p>}
          </div>

          {images.length > 0 && (
            <div className="img-grid" style={{ marginTop: 16 }}>
              {images.map((f) => (
                <div className="img-card" key={f}>
                  <div className="img-card__thumb">
                    <img src={imgUrl(f)} alt={det.name || f} loading="lazy" />
                  </div>
                </div>
              ))}
            </div>
          )}

          <h2 className="detail__subhead">Itinerary ({days.length} days)</h2>
          {days.length === 0 ? (
            <div className="state state--empty">No day-by-day plan.</div>
          ) : (
            days.map((d) => {
              const timeline = Array.isArray(d.timeline) ? d.timeline : [];
              return (
                <div key={d.day} className="day-card">
                  <h3>Day {d.day}{d.theme ? `: ${d.theme}` : ''}</h3>
                  {d.day_summary && <p className="day-card__meta">{d.day_summary}</p>}
                  {timeline.length > 0 && (
                    <ul className="timeline">
                      {timeline.map((t, i) => (
                        <li key={i} className="timeline__item">
                          <span className="timeline__time">{t.suggested_time || '—'}</span>
                          <span className="timeline__body">
                            <strong>{t.name || t.event || t.type || 'Activity'}</strong>
                            {t.duration && <span className="muted"> · {t.duration}</span>}
                            {t.note && <div className="muted">{t.note}</div>}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })
          )}

          {hotels.length > 0 && (
            <>
              <h2 className="detail__subhead">Hotels</h2>
              {hotels.map((h, i) => {
                const s = h.selected || {};
                return (
                  <div key={i} className="day-card">
                    <h3>{s.name || 'Hotel'} <span className="muted">(Day {h.from_day}–{h.to_day})</span></h3>
                    <p className="day-card__meta">
                      {[s.type, s.price_range, s.rating && `★ ${s.rating}`].filter(Boolean).join(' · ') || '—'}
                    </p>
                    {s.reason && <p className="muted">{s.reason}</p>}
                  </div>
                );
              })}
            </>
          )}
        </>
      )}
    </div>
  );
}
